/* ──────────────────────────────────────────────────────────────────────
   leadBus — global pub-sub for lead state changes.

   Every lead-displaying surface in the CRM (the leads table, the lead
   detail page, the pipeline kanban, the calendar, the dashboard, the
   drawer) maintains its own local `useState`. That worked fine until
   we noticed: when Williams sets a lead to "Contacted" inside the
   detail page, the leads table in Superadmin's session is stale.

   This module fixes that without introducing React Query / Redux /
   Zustand. Every mutation helper in `leadApi.ts` emits a small event
   after a successful response; every surface subscribes and refetches.

   Two transports are layered:
     1. In-process listeners (Set<Handler>) — same-tab broadcast.
     2. `BroadcastChannel("dcc-leads")` — cross-tab broadcast so a user
        with two tabs open in the same browser sees changes in both.

   Safe in environments without `BroadcastChannel` (older Safari, some
   embedded webviews): the API gracefully degrades to in-process only.
   ────────────────────────────────────────────────────────────────────── */

/** All event types the bus knows about. Adding a new mutation in
 *  leadApi.ts should also add the corresponding event type here so
 *  the `type` field stays a literal union. */
export type LeadBusEventType =
  | "lead.updated"      // any field change (status, assignee, fields, etc.)
  | "lead.created"      // new lead in the system
  | "lead.deleted"      // hard delete (rare)
  | "lead.archived"     // soft delete / archive / merge / trash
  | "lead.restored"     // restored from archive / trash
  | "lead.assigned"     // assigned_to changed (claim, reassign)
  | "lead.status"       // status changed
  | "lead.note"         // note added / removed
  | "lead.same_event"   // same-event grouping changed
  | "lead.primary"      // primary-event flag changed
  | "lead.refresh-all"; // catch-all "everything is dirty" signal

export interface LeadBusEvent {
  type: LeadBusEventType;
  /** Affected lead id (when applicable — refresh-all carries 0). */
  leadId: number;
  /** Optional fresh lead payload — surfaces can patch their own state
   *  without an extra round-trip when this is present. */
  lead?: any;
  /** Optional extra context (e.g. previous assignee, source of update). */
  meta?: Record<string, any>;
  /** Source tab id — used to skip echo when an event comes back to us
   *  via the cross-tab BroadcastChannel. */
  source?: string;
}

type Handler = (event: LeadBusEvent) => void;

const TAB_ID = (() => {
  // Stable per-tab id so we can ignore our own cross-tab echoes.
  try {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  } catch {
    return "tab";
  }
})();

const handlers: Set<Handler> = new Set();

/* ── Cross-tab transport ─────────────────────────────────────────────
   Constructed lazily — older browsers and SSR contexts may not have
   `BroadcastChannel`. Inbound messages are dispatched to in-process
   handlers exactly like a local emit, except we mark them with the
   originating tab so subscribers can filter if they want to. */
let bc: BroadcastChannel | null = null;
try {
  if (typeof window !== "undefined" && "BroadcastChannel" in window) {
    bc = new BroadcastChannel("dcc-leads");
    bc.onmessage = (msg) => {
      const ev = msg?.data as LeadBusEvent | undefined;
      if (!ev || typeof ev.type !== "string") return;
      if (ev.source === TAB_ID) return;             // ignore our own echo
      handlers.forEach((h) => {
        try { h(ev); } catch (err) { /* keep going */ console.error("leadBus handler error", err); }
      });
    };
  }
} catch {
  bc = null;
}

/** Fire an event. Same-tab subscribers run synchronously; the cross-tab
 *  channel posts the message asynchronously (browser-managed). */
export function emit(event: Omit<LeadBusEvent, "source">): void {
  const payload: LeadBusEvent = { ...event, source: TAB_ID };
  // 1. In-process broadcast.
  handlers.forEach((h) => {
    try { h(payload); } catch (err) { console.error("leadBus handler error", err); }
  });
  // 2. Cross-tab broadcast (best-effort).
  try { bc?.postMessage(payload); } catch { /* silent */ }
}

/** Subscribe to every lead event. Returns an unsubscribe function.
 *  Prefer the `useLeadBus()` hook in React components — it wraps this
 *  with proper cleanup on unmount. */
export function subscribe(handler: Handler): () => void {
  handlers.add(handler);
  return () => { handlers.delete(handler); };
}

/* ── Convenience shorthands for the common emits ────────────────────
   Used by leadApi.ts mutation helpers. Keeps the call-site short. */

export const emitLeadUpdated   = (leadId: number, lead?: any, meta?: Record<string, any>) =>
  emit({ type: "lead.updated", leadId, lead, meta });

export const emitLeadStatus    = (leadId: number, lead?: any, meta?: Record<string, any>) =>
  emit({ type: "lead.status", leadId, lead, meta });

export const emitLeadAssigned  = (leadId: number, lead?: any, meta?: Record<string, any>) =>
  emit({ type: "lead.assigned", leadId, lead, meta });

export const emitLeadNote      = (leadId: number, meta?: Record<string, any>) =>
  emit({ type: "lead.note", leadId, meta });

export const emitLeadArchived  = (leadId: number, meta?: Record<string, any>) =>
  emit({ type: "lead.archived", leadId, meta });

export const emitLeadRestored  = (leadId: number, meta?: Record<string, any>) =>
  emit({ type: "lead.restored", leadId, meta });

export const emitLeadSameEvent = (leadId: number, meta?: Record<string, any>) =>
  emit({ type: "lead.same_event", leadId, meta });

export const emitLeadPrimary   = (leadId: number, meta?: Record<string, any>) =>
  emit({ type: "lead.primary", leadId, meta });

export const emitLeadCreated   = (leadId: number, lead?: any) =>
  emit({ type: "lead.created", leadId, lead });
